import React from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatDate, formatDayMonth, formatSignedNumber, formatTime } from './clientCard.format';
import { VitalPoint, VitalSeries, vitalDelta } from './clientCard.model';

/** The card's data colour, so the big chart and the row's sparkline are the same line. */
const LINE = 'rgb(2 132 199)';

const PLOT = { width: 680, height: 300, left: 52, right: 20, top: 18, bottom: 34 };
const INNER_WIDTH = PLOT.width - PLOT.left - PLOT.right;
const INNER_HEIGHT = PLOT.height - PLOT.top - PLOT.bottom;

/**
 * How much horizontal room a date label needs before the next one may be drawn.
 *
 * Sized for the widest label the axis can hold — a full `DD/MM/YYYY` — because the
 * dates carry their year now and 58px was cut for `DD/MM`.
 */
const MIN_LABEL_GAP = 76;

/**
 * Which way a date label hangs off its tick.
 *
 * Centred on the tick everywhere except the two ends: the last point sits ON the
 * right edge of the plot, so a centred `DD/MM/YYYY` there put half its width into
 * the margin and the year came out cropped.
 */
const anchorFor = (x: number): 'start' | 'middle' | 'end' => {
  if (x < PLOT.left + 30) return 'start';
  if (x > PLOT.width - PLOT.right - 30) return 'end';
  return 'middle';
};

/**
 * Round tick values that fall INSIDE the band, never outside it.
 *
 * The first cut let the nice step start below the band and the axis ran to -50 for a
 * heart rate: half the plot spent on impossible values, and the real movement
 * squeezed into the top third. So ticks begin at the first round multiple at or
 * above the floor, and the band itself is what the data asked for.
 */
export const niceTicks = (low: number, high: number, count = 4): number[] => {
  const rawStep = (high - low) / count;
  if (!Number.isFinite(rawStep) || rawStep <= 0) return [low];

  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step =
    [1, 2, 2.5, 5, 10].map((multiple) => multiple * magnitude).find((candidate) => candidate >= rawStep) ?? rawStep;

  const ticks: number[] = [];
  for (let tick = Math.ceil(low / step) * step; tick <= high + step / 1000; tick += step) {
    ticks.push(Number(tick.toFixed(6)));
  }
  return ticks.length > 0 ? ticks : [low];
};

type Hovered = { point: VitalPoint; x: number; y: number };

/**
 * One vital's whole series, big enough to read.
 *
 * The row's sparkline answers "which way is this going"; this answers "what were the
 * numbers, and who took them". Points sit at the instant they were measured, as in
 * the sparkline: evenly spacing three readings from one week and one from a month
 * later would draw a steady four-step trend that never happened.
 *
 * Hover gives a crosshair and a readout, and the measurement list underneath carries
 * the same three facts for every point — so no value is reachable only by pointing at
 * it, and a reader on a keyboard is not shut out.
 */
const VitalChartModal: React.FC<{ series: VitalSeries | null; onClose: () => void }> = ({ series, onClose }) => {
  const { t, i18n } = useTranslation();
  const [hovered, setHovered] = React.useState<Hovered | null>(null);

  React.useEffect(() => setHovered(null), [series]);

  const geometry = React.useMemo(() => {
    if (!series || series.points.length === 0) return null;

    const values = series.points.map((point) => point.value);
    const times = series.points.map((point) => new Date(point.at).getTime());

    const min = Math.min(...values);
    const max = Math.max(...values);
    // A flat series still needs a band, or every point lands on one edge.
    const pad = (max - min) * 0.15 || Math.abs(max) * 0.1 || 1;
    // Padding never takes a measurement that cannot go negative below zero: no
    // caregiver has ever recorded -10 mmHg, and an axis that offers the room says
    // the chart does not know what it is plotting.
    const axisLow = min >= 0 ? Math.max(0, min - pad) : min - pad;
    const axisHigh = max + pad;

    const ticks = niceTicks(axisLow, axisHigh);

    const firstTime = times[0];
    const span = times[times.length - 1] - firstTime;

    const x = (time: number) =>
      span === 0 ? PLOT.left + INNER_WIDTH / 2 : PLOT.left + ((time - firstTime) / span) * INNER_WIDTH;
    const y = (value: number) => PLOT.top + INNER_HEIGHT - ((value - axisLow) / (axisHigh - axisLow)) * INNER_HEIGHT;

    return {
      ticks,
      placed: series.points.map((point, index) => ({ point, x: x(times[index]), y: y(point.value) })),
      yOf: y,
      axisLow,
      axisHigh,
    };
  }, [series]);

  const numberFormat = React.useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language]
  );

  if (!series || !geometry) return null;

  const { ticks, placed } = geometry;
  const latest = placed[placed.length - 1];
  const delta = vitalDelta(series);

  // Labelled by position and not by index: the points sit where they were measured,
  // so every third one can still be three days apart and print on top of its
  // neighbour. The most recent date is always kept — it is the one being read.
  const xLabels = placed.reduce<typeof placed>((kept, spot) => {
    const previous = kept[kept.length - 1];
    if (!previous || spot.x - previous.x >= MIN_LABEL_GAP) return [...kept, spot];
    if (spot === latest) return [...kept.slice(0, -1), spot];
    return kept;
  }, []);

  const active = hovered ?? latest;

  const findNearest = (event: React.PointerEvent<SVGRectElement>) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;

    const bounds = svg.getBoundingClientRect();
    // The plot is drawn at PLOT.width and scaled to fit, so the pointer has to be
    // put back into the viewBox's own units before it can be compared to a point.
    const pointerX = ((event.clientX - bounds.left) / bounds.width) * PLOT.width;

    const nearest = placed.reduce((closest, candidate) =>
      Math.abs(candidate.x - pointerX) < Math.abs(closest.x - pointerX) ? candidate : closest
    );
    setHovered(nearest);
  };

  return (
    <Modal
      open={!!series}
      onCancel={onClose}
      footer={null}
      width={740}
      title={
        <span className="flex flex-wrap items-baseline gap-2">
          <span>{t(series.labelKey)}</span>
          {series.unit && <span className="text-[12px] font-normal text-slate-400">{series.unit}</span>}
          <span className="text-[12px] font-normal text-slate-400">
            {t('client-card-vital-measurements', { count: series.points.length })}
          </span>
        </span>
      }
    >
      <div className="relative">
        <svg
          viewBox={`0 0 ${PLOT.width} ${PLOT.height}`}
          className="w-full"
          style={{ height: 'auto' }}
          role="img"
          aria-label={t(series.labelKey)}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              {/* Solid hairlines, one shade off the surface: a dashed grid reads as a
                  threshold, and there is no threshold here. */}
              <line
                x1={PLOT.left}
                x2={PLOT.width - PLOT.right}
                y1={geometry.yOf(tick)}
                y2={geometry.yOf(tick)}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
              <text
                x={PLOT.left - 8}
                y={geometry.yOf(tick) + 3.5}
                textAnchor="end"
                className="fill-slate-400 text-[10px] tabular-nums"
              >
                {numberFormat.format(tick)}
              </text>
            </g>
          ))}

          {xLabels.map((spot) => (
            <text
              key={`x-${spot.point.visitId}`}
              x={spot.x}
              y={PLOT.height - 12}
              textAnchor={anchorFor(spot.x)}
              className="fill-slate-400 text-[10px] tabular-nums"
            >
              {formatDayMonth(spot.point.at, i18n.language)}
            </text>
          ))}

          {placed.length > 1 && (
            <polyline
              points={placed.map((spot) => `${spot.x},${spot.y}`).join(' ')}
              fill="none"
              stroke={LINE}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {hovered && (
            <line
              x1={hovered.x}
              x2={hovered.x}
              y1={PLOT.top}
              y2={PLOT.top + INNER_HEIGHT}
              stroke="#94a3b8"
              strokeWidth="1"
            />
          )}

          {placed.map((spot) => (
            // A 2px surface ring, so two readings a day apart stay two readings.
            <circle
              key={spot.point.visitId}
              cx={spot.x}
              cy={spot.y}
              r={active.point.visitId === spot.point.visitId ? 5.5 : 4}
              fill={LINE}
              stroke="#ffffff"
              strokeWidth="2"
            />
          ))}

          {/* Only the latest is labelled on the plot. A number beside every point is
              chaos; the axis, the crosshair and the list below carry the rest. */}
          <text
            x={Math.min(latest.x + 10, PLOT.width - PLOT.right)}
            y={latest.y - 10}
            textAnchor={latest.x > PLOT.width - PLOT.right - 60 ? 'end' : 'start'}
            className="fill-slate-700 text-[12px] font-semibold tabular-nums"
          >
            {numberFormat.format(latest.point.value)}
          </text>

          <rect
            x={PLOT.left}
            y={PLOT.top}
            width={INNER_WIDTH}
            height={INNER_HEIGHT}
            fill="transparent"
            onPointerMove={findNearest}
            onPointerLeave={() => setHovered(null)}
          />
        </svg>

        {hovered && (
          <div
            // Flipped below the point when the point is high, so a reading near the top
            // of the band does not put its own readout over the chart's title.
            className={`pointer-events-none absolute z-10 -translate-x-1/2 rounded-md bg-slate-800 px-2.5 py-1.5 text-[11px] leading-snug text-white shadow-lg ${
              hovered.y < PLOT.top + INNER_HEIGHT * 0.35 ? 'translate-y-3' : '-translate-y-[calc(100%+12px)]'
            }`}
            style={{ left: `${(hovered.x / PLOT.width) * 100}%`, top: `${(hovered.y / PLOT.height) * 100}%` }}
          >
            {/* The value leads: the reader already knows which vital they opened. */}
            <div className="text-[13px] font-semibold tabular-nums">
              {numberFormat.format(hovered.point.value)}
              {series.unit && <span className="ml-1 text-[10px] font-normal text-slate-300">{series.unit}</span>}
            </div>
            <div className="text-slate-300">
              {`${formatDate(hovered.point.at, i18n.language)} ${formatTime(hovered.point.at, i18n.language)}`}
            </div>
            <div className="text-slate-300">
              {hovered.point.caregivers.join(', ') || t('dashboard-note-unassigned-caregiver')}
            </div>
          </div>
        )}
      </div>

      {delta !== null && (
        <div className="mt-1 text-[12px] text-slate-500">
          {delta === 0
            ? t('client-card-vital-unchanged-since', { date: formatDayMonth(series.points[0].at, i18n.language) })
            : t('client-card-vital-change-since', {
                change: formatSignedNumber(delta, i18n.language),
                date: formatDayMonth(series.points[0].at, i18n.language),
              })}
        </div>
      )}

      <div className="mt-4">
        <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
          {t('client-card-vital-readings')}
        </div>
        {/* The chart's table twin: every value the crosshair shows is here without a
            pointer, newest first because that is the one being asked about. */}
        <div className="max-h-56 overflow-y-auto">
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 bg-white text-left text-[11px] text-slate-400">
              <tr>
                <th className="py-1 pr-3 font-medium">{t('date')}</th>
                <th className="py-1 pr-3 text-right font-medium">{t('client-card-vital-value')}</th>
                <th className="py-1 font-medium">{t('caregiver')}</th>
              </tr>
            </thead>
            <tbody>
              {[...series.points].reverse().map((point) => (
                <tr key={point.visitId} className="border-t border-slate-100 text-slate-600">
                  <td className="py-1.5 pr-3 tabular-nums whitespace-nowrap">
                    {`${formatDate(point.at, i18n.language)} ${formatTime(point.at, i18n.language)}`}
                  </td>
                  <td className="py-1.5 pr-3 text-right font-medium tabular-nums text-slate-800">
                    {numberFormat.format(point.value)}
                  </td>
                  <td className="py-1.5">
                    {point.caregivers.join(', ') || (
                      <span className="italic text-slate-400">{t('dashboard-note-unassigned-caregiver')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};

export default VitalChartModal;
